// Service location types for SVG Map
import type { ManageAPI } from "~/manage-api/types";

export interface ServiceLocationData {
  id: number;
  name: string;
  code: string;
  x: number;
  y: number;
  floorId: number;
  serviceType: number;
  originalData: ManageAPI.ServiceLocation;
}

/**
 * Service location configuration interface
 */
export interface ServiceLocationConfig {
  enabled: boolean;
  showServicePins: boolean;
  // Coordinate adjustment for alignment with backend map editor
  coordinateOffset: {
    x: number;
    y: number;
  };
  // Scaling transformation for coordinate system alignment
  coordinateScaling: {
    enabled: boolean;
    scaleFactor: number;
    centerX: number;
    centerY: number;
  };
  pinStyling: {
    scale: number;
    fillColor: {
      inactive: string;
      active: string;
    };
    strokeColor: {
      inactive: string;
      active: string;
    };
    strokeWidth: {
      inactive: string;
      active: string;
    };
    yOffset: number;
    showIconBackground: boolean; // Show background circle behind custom icons
    iconSize: number; // Base size for custom icons
  };
  // Google Material Icons styling
  googleIconStyling: {
    color: {
      inactive: string;
      active: string;
    };
  };
  // Icon background circle styling
  iconBackgroundStyling: {
    radius: number;
    fillColor: {
      inactive: string;
      active: string;
    };
    strokeColor: {
      inactive: string;
      active: string;
    };
    strokeWidth: {
      inactive: string;
      active: string;
    };
  };
  labelStyling: {
    enabled: boolean;
    fontSize: string;
    fontWeight: string;
    textColor: string;
    textShadow: string;
    yOffset: number;
    showLabelOnHover: boolean;
    hoverBackgroundColor: string;
    hoverBorderRadius: string;
    hoverVerticalPadding: string;
    hoverHorizontalPadding: string;
  };
  // Custom icons from fragment editable images
  customIcons?: {
    [key in ServiceTypeCode]?: {
      imageUrl: string | null;
      googleIcon?: string; // Google Material Icon name
    };
  };
}

export type ServiceTypeCode = 
  | 'parking'
  | 'toilet'
  | 'elevator'
  | 'atm'
  | 'escalator'
  | 'stairs'
  | 'taxi'
  | 'recycling'
  | 'train'
  | 'metro'
  | 'tram'
  | 'bus'
  | 'stop'
  | 'nursery_room'
  | 'service_desk'
  | 'directory'
  | 'accessible_wc'
  | 'bottle_recycling'
  | 'playground';

export interface ServiceTypeInfo {
  id: number;
  name: string;
  code: ServiceTypeCode;
  icon?: string;
  color?: string;
}

/**
 * Service location state interface - simplified for static display
 */
export interface ServiceLocationState {
  visibleServiceLocations: ServiceLocationData[];
}

// Default service type mappings
export const DEFAULT_SERVICE_TYPES: ServiceTypeInfo[] = [
  { id: 5, name: "Parking", code: "parking" },
  { id: 6, name: "Toilet", code: "toilet" },
  { id: 7, name: "Elevator", code: "elevator" },
  { id: 8, name: "ATM", code: "atm" },
  { id: 9, name: "Escalator", code: "escalator" },
  { id: 10, name: "Stairs", code: "stairs" },
  { id: 11, name: "Taxi", code: "taxi" },
  { id: 12, name: "Recycling", code: "recycling" },
  { id: 13, name: "Train", code: "train" },
  { id: 14, name: "Metro", code: "metro" },
  { id: 15, name: "Tram", code: "tram" },
  { id: 16, name: "Bus", code: "bus" },
  { id: 17, name: "Stop", code: "stop" },
  { id: 18, name: "Nursery room", code: "nursery_room" },
  { id: 20, name: "Service desk", code: "service_desk" },
  { id: 21, name: "Directory", code: "directory" },
  { id: 22, name: "Accessible WC", code: "accessible_wc" },
  { id: 23, name: "Bottle recycling", code: "bottle_recycling" },
  { id: 24, name: "Playground", code: "playground" },
];

// Default configuration
export const DEFAULT_SERVICE_LOCATION_CONFIG: ServiceLocationConfig = {
  enabled: true,
  showServicePins: true,
  // Default coordinate offset - adjust these values to align with backend map editor
  coordinateOffset: {
    x: 0,
    y: 0,
  },
  // Default coordinate scaling - adjust these values to align coordinate systems
  coordinateScaling: {
    enabled: true,
    scaleFactor: 0.7,
    centerX: 500,
    centerY: 400,
  },
  pinStyling: {
    scale: 1.0,
    fillColor: {
      inactive: '#FFFFFF',
      active: '#FF6B35',
    },
    strokeColor: {
      inactive: '#FF6B35',
      active: '#FFFFFF',
    },
    strokeWidth: {
      inactive: '1px',
      active: '2px',
    },
    yOffset: 0,
    showIconBackground: true,
    iconSize: 20,
  },
  
  // Google Material Icons styling
  googleIconStyling: {
    color: {
      inactive: '#333333',
      active: '#0066cc',
    },
  },
  
  // Icon background circle styling
  iconBackgroundStyling: {
    radius: 10,
    fillColor: {
      inactive: '#FFFFFF',
      active: '#F0F0F0',
    },
    strokeColor: {
      inactive: '#333333',
      active: '#0066cc',
    },
    strokeWidth: {
      inactive: '1',
      active: '2',
    },
  },
  
  labelStyling: {
    enabled: true,
    fontSize: '12px',
    fontWeight: 'bold',
    textColor: '#000000',
    textShadow: '0 0 2px rgba(255,255,255,0.8)',
    yOffset: 0,
    showLabelOnHover: true,
    hoverBackgroundColor: 'rgba(255, 255, 255, 0.9)',
    hoverBorderRadius: '4px',
    hoverVerticalPadding: '2px',
    hoverHorizontalPadding: '4px',
  },
}; 